import React from "react";
import { Alert, Box, Checkbox, Tooltip, Typography } from "@mui/material";
import { useLang } from "../i18n";
import TargetName from "./TargetName";
import { buckets, packageDeployValue, packageStageDeployValue, vorwaertsAlleZiele } from "../context/TransportV2Context";
import { designTypeLabel, isPackageTransport, packageDesignObjects, packageIflowIds } from "../utils/packageTransport";
import { isArtifactDraft } from "../utils/sameVersion";
import { stageLabel } from "../../../utils/stages";
import { packageDraftHint, packageFallbackText, packageTransportText, removedInTargetText, transportPackageError } from "../../../utils/transportPackageErrors";

// Pakettransport (W6.5): Abschnitt „Ganzes Paket wird transportiert“ in Schritt 3 mit dem Umfang (iFlows und Designobjekte).
// Löschen und Neuimport im Ziel sind SAPs Standardweg und stehen seit Welle 6 nicht mehr als Warnung da. Die iFlows stehen darunter in der bekannten Tabelle,
// ebenfalls fest ausgewählt. Farben nur über var(--iio-*), Fokus und Hover ohne Glow.

// Sichtbarer Grund der festen Auswahl; jede feste Zeile verweist per aria-describedby darauf.
export const FIXED_REASON_ID = "s3-whole-package-reason";

/** Singular und Plural der Zähler (Schlüssel mit _one und _other). */
export const countText = (t, key, n) => t(`${key}_${n === 1 ? "one" : "other"}`, { n });
export const scopeText = (t, iflows, design) => `${countText(t, "pkgT.iflows", iflows)} · ${countText(t, "pkgT.design", design)}`;

export const fixedChoiceSx = {
  p: 0.5, color: "var(--iio-text-3)",
  "&.Mui-checked.Mui-disabled, &.Mui-disabled": { color: "var(--iio-text-3)" },
  "&:hover": { backgroundColor: "transparent" },
};

export function FixedChoice({ label, ariaLabel }) {
  const { t } = useLang();
  return <Tooltip title={t("pkgT.fixed")} disableInteractive>
    <Box component="span" sx={{ display: "inline-flex" }}>
      <Checkbox checked disabled disableRipple size="small" sx={fixedChoiceSx}
        inputProps={{ "aria-label": ariaLabel || t("pkgT.fixedLabel", { name: label }), "aria-describedby": FIXED_REASON_ID }} />
    </Box>
  </Tooltip>;
}


/** MUI Alert Warnung wenn CI für mindestens eine Stufe aus ist. */
export function PackageCiWarning({ state }) {
  const { t } = useLang();
  if (!isPackageTransport(state)) return null;
  const stages = vorwaertsAlleZiele(state);
  const offStages = stages.filter(stage => !packageStageDeployValue(state, stage.key));
  if (!offStages.length) return null;
  const allOff = offStages.length === stages.length;
  const text = allOff ? t("pkgT.ciWarningAll")
    : t("pkgT.ciWarningStages", { stages: offStages.map(s => stageLabel(s.key) || s.code || s.key.toUpperCase()).join(", ") });
  return <Alert severity="warning" variant="outlined" role="status"
    sx={{ borderRadius: "6px", fontFamily: "inherit", fontSize: 13, color: "var(--iio-text-1)", backgroundColor: "transparent",
      borderColor: "var(--iio-warning)", "& .MuiAlert-icon": { color: "var(--iio-warning)" } }}>
    {text}
  </Alert>;
}

export default function WholePackageSection({ designObjects, iflowCount, state }) {
  const { t } = useLang();
  const headingId = "s3-whole-package";
  return <Box component="section" className="panel" aria-labelledby={headingId} sx={{ mb: 2 }}>
    <div className="panel-h">
      <h3 id={headingId}>{t("pkgT.title")}</h3>
      <span className="meta">{scopeText(t, iflowCount, designObjects.length)}</span>
    </div>
    <Box className="panel-pad" sx={{ pt: 1.5, display: "grid", gap: 1.5 }}>
      <Typography id={FIXED_REASON_ID} component="p" sx={{ m: 0, fontFamily: "inherit", fontSize: 13, color: "var(--iio-text-2)" }}>
        {t("pkgT.info")}
      </Typography>
      {state && <PackageCiWarning state={state} />}
    </Box>
  </Box>;
}

// Schritt 5: Transportart und Zielpaket je Werk. Namen und IDs ausschließlich aus dem Vergleich des Servers.
export function WholePackageReview({ state }) {
  const { t } = useLang();
  const targetId = state.space?.qasInstance?.id;
  const plants = buckets(state);
  const iflows = packageIflowIds(state).length;
  const design = packageDesignObjects(state).length;
  const reviewEntries = Object.values(state.precheckByBucket || {}).filter(entry => entry && !entry.error && Array.isArray(entry.comparison));
  // Ohne Warnung: Löschen und Neuimport sind der Standardweg. Die Linie bleibt neutral, nur „Wird im Ziel entfernt“ warnt.
  return <Box component="section" className="sect" aria-label={t("pkgT.mode")}
    sx={{ borderLeft: "2px solid var(--iio-border)", pl: 1.5, py: 0.5, color: "var(--iio-text-1)" }}>
    <Typography component="h3" sx={{ m: 0, fontFamily: "inherit", fontSize: 15, fontWeight: 600 }}>
      {t("pkgT.mode")}: {t("pkgT.modeWhole")}
    </Typography>
    <Typography component="p" sx={{ m: 0, fontFamily: "inherit", fontSize: 13, color: "var(--iio-text-2)" }}>
      {scopeText(t, iflows, design)}
    </Typography>
    {hasTargetRemoval(reviewEntries) && <Box sx={{ mt: 1 }}><TargetRemovalNotice entries={reviewEntries} /></Box>}
    <Box component="ul" sx={{ listStyle: "none", m: 0, mt: 0.75, p: 0, fontSize: 13 }}>
      {plants.map(plant => {
        const entry = state.precheckByBucket?.[`${targetId}__${plant.kennung || "std"}`];
        const name = typeof entry?.targetPackageName === "string" && entry.targetPackageName
          ? entry.targetPackageName : (!plant.kennung ? state.pkgName || state.pkgId : null);
        const id = entry?.targetPackageId || (!plant.kennung ? state.pkgId : null);
        return <Box component="li" key={plant.key} sx={{ overflowWrap: "anywhere", "& + &": { mt: 0.5 } }}>
          {plant.kennung && <Box component="span" sx={{ color: "var(--iio-text-3)" }}>{plant.name} · <span className="ken">{plant.kennung}</span>: </Box>}
          <Box component="strong" sx={{ fontWeight: 600 }}>{name
            ? (plant.kennung ? <TargetName source={state.pkgName || state.pkgId} target={name} kennung={plant.kennung} /> : name)
            : t("pkgT.renamed")}</Box>
          {id && id !== name && <Box component="span" sx={{ color: "var(--iio-text-3)" }}> · {id}</Box>}
        </Box>;
      })}
    </Box>
  </Box>;
}

const noteSx = { borderRadius: "6px", fontFamily: "inherit", fontSize: 13, color: "var(--iio-text-1)", backgroundColor: "transparent",
  "& .MuiAlert-icon": { color: "var(--iio-text-2)" } };

// Schritt 3: Meldungen des Servers zum Pakettransport, mit denselben Texten wie Vorprüfung und Snapshot.
// Nichts davon sperrt Weiter: die Entscheidung trifft der Server (etwa mit freigegebenen Entwürfen).
export function PackageTransportNotes({ state }) {
  const { lang } = useLang();
  const entries = Object.values(state.precheckByBucket || {}).filter(entry => entry && !entry.error && Array.isArray(entry.comparison));
  if (!entries.length) return null;
  const whole = isPackageTransport(state);
  const notes = [];
  if (whole) {
    const drafts = [...new Set(entries.flatMap(entry => entry.comparison.filter(isArtifactDraft).map(a => a.Name || a.originalId || a.Id)))];
    // Hinweis, keine Sperre: der Server entscheidet (mit freigegebenen Entwürfen lässt er sie zu).
    if (drafts.length) notes.push({ key: "draft", severity: "warning", text: packageDraftHint(drafts, lang) });
    // M6: Paket-ID, die der Pakettransport nicht übertragen kann.
    const invalid = entries.flatMap(entry => entry.notices || []).find(notice => codeOfNotice(notice) === "PACKAGE_TRANSPORT_ID_INVALID");
    if (invalid) notes.push({ key: "idInvalid", severity: "warning",
      text: (typeof invalid?.message === "string" && invalid.message.trim()) || transportPackageError("PACKAGE_TRANSPORT_ID_INVALID", lang) });
    // DEV181 C1: Eine Ziel-ID liegt im Ziel in einem anderen Paket. Der Server sperrt das ganze Paket; Text vom Server.
    for (const entry of entries) if (entry.packageTransportBlocked?.message) notes.push({ key: `blocked:${entry.werk?.kennung || "std"}`, severity: "error",
      text: entry.packageTransportBlocked.message });
    for (const entry of entries) if (entry.plantRenameFailed) notes.push({ key: `rename:${entry.werk?.kennung || "std"}`, severity: "warning",
      text: packageTransportText("plantRenameFailed", lang, { werk: [entry.werk?.name, entry.werk?.kennung].filter(Boolean).join(" ") || "-",
        message: entry.plantRenameFailed.message || entry.plantRenameFailed.code || "" }) });
  } else {
    // Export nicht lesbar oder zu groß: Das Paket läuft je iFlow weiter (Artefaktweg mit Hinweis).
    const unavailable = entries.find(entry => entry.packageExportUnavailable)?.packageExportUnavailable;
    if (unavailable) notes.push({ key: "unavailable", severity: "info",
      text: packageFallbackText(unavailable.code === "PACKAGE_EXPORT_TOO_LARGE" ? "PACKAGE_EXPORT_TOO_LARGE" : "PACKAGE_EXPORT_UNAVAILABLE", lang) });
    const unclear = entries.find(entry => entry.packageExportUnclear)?.packageExportUnclear;
    if (unclear) notes.push({ key: "unclear", severity: "info", text: transportPackageError({ code: "PACKAGE_EXPORT_UNCLEAR", body: { error: unclear.message } }, lang) });
  }
  // M7: Die Sperre (packageTransportBlocked) steht oben je Werk; Weiter sperrt Scene3 über packageTransportBlockOf.
  // I2: Eigentümer der IDs im Ziel nicht vollständig lesbar, keine Sperre.
  if (entries.some(entry => entry.packageOwnersUnverified === true))
    notes.push({ key: "owners", severity: "warning", text: packageTransportText("PACKAGE_OWNERS_UNVERIFIED", lang) });
  const codeOf = notice => (typeof notice === "string" ? notice : notice?.code);
  if (entries.some(entry => (entry.notices || []).some(notice => codeOf(notice) === "PACKAGE_TRANSPORT_SWITCHED_TO_ARTIFACT")))
    notes.push({ key: "switched", severity: "info", text: transportPackageError("PACKAGE_TRANSPORT_SWITCHED_TO_ARTIFACT", lang) });
  const removal = whole && hasTargetRemoval(entries);
  if (!notes.length && !removal) return null;
  return <Box sx={{ display: "grid", gap: 1, mb: 2 }}>
    {removal && <TargetRemovalNotice entries={entries} />}
    {notes.map(note => <Alert key={note.key} severity={note.severity} variant="outlined" role={note.severity === "error" ? "alert" : "status"}
      sx={{ ...noteSx, borderColor: `var(--iio-${note.severity === "error" ? "critical" : note.severity === "warning" ? "warning" : "border"})`,
        ...(note.severity !== "info" && { "& .MuiAlert-icon": { color: `var(--iio-${note.severity === "error" ? "critical" : "warning"})` } }) }}>
      {note.text}
    </Alert>)}
  </Box>;
}

/** Text der Sperre packageTransportBlocked aus dem Precheck oder null. */
export function packageTransportBlockOf(entries) {
  const blocked = (entries || []).find(entry => entry && !entry.error && entry.packageTransportBlocked)?.packageTransportBlocked;
  if (!blocked) return null;
  return (typeof blocked.message === "string" && blocked.message.trim()) || transportPackageError(blocked.code) || packageTransportText("PACKAGE_TRANSPORT_BLOCKED_NEXT");
}

const codeOfNotice = notice => (typeof notice === "string" ? notice : notice?.code);

/**
 * Vor dem Start (Scene3 und Scene5): die Objekte, die beim Neuimport im Ziel verloren gehen, je Ziel bzw. Werk.
 * Felder des Precheck je Eintrag: removedInTarget[{ id, name, type }], removedInTargetUnknown, removedInTargetPartial.
 * Nur Hinweis, es sperrt nichts. Das Löschen selbst (PACKAGE_TRANSPORT_TARGET_REPLACED) ist SAPs Standardweg und erscheint
 * seit Welle 6 nicht mehr als eigene Warnung.
 */
function hasTargetRemoval(entries) {
  return entries.some(entry => entry.removedInTargetUnknown === true
    || (Array.isArray(entry.removedInTargetPartial) && entry.removedInTargetPartial.length > 0)
    || (Array.isArray(entry.removedInTarget) && entry.removedInTarget.some(item => item && (item.id || item.name))));
}

export function TargetRemovalNotice({ entries }) {
  const { lang } = useLang();
  const rows = entries.flatMap(entry => (Array.isArray(entry.removedInTarget) ? entry.removedInTarget : [])
    .filter(item => item && (item.id || item.name))
    .map(item => ({ item, plant: entry.werk?.kennung ? [entry.werk.name, entry.werk.kennung].filter(Boolean).join(" ") : "" })));
  const unknown = entries.some(entry => entry.removedInTargetUnknown === true);
  const partial = [...new Set(entries.flatMap(entry => (Array.isArray(entry.removedInTargetPartial) ? entry.removedInTargetPartial : [])
    .map(type => designTypeLabel(type)).filter(Boolean)))];
  if (!rows.length && !unknown && !partial.length) return null;
  return <Alert severity="warning" variant="outlined" role="status" sx={{ ...noteSx, borderColor: "var(--iio-warning)",
    "& .MuiAlert-icon": { color: "var(--iio-warning)" } }}>
    {rows.length > 0 && <>
      <Box component="p" sx={{ m: 0 }}>{packageTransportText("PACKAGE_TRANSPORT_TARGET_REMOVED_TITLE", lang)}</Box>
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.25 }}>
        {rows.map(({ item, plant }) => <li key={`${plant}:${item.id || item.name}`}>
          {plant && <Box component="span" sx={{ color: "var(--iio-text-2)" }}>{plant}: </Box>}{removedInTargetText(item, lang)}
        </li>)}
      </Box>
    </>}
    {unknown && <Box component="p" sx={{ m: 0, mt: rows.length ? 0.5 : 0 }}>{packageTransportText("PACKAGE_TRANSPORT_TARGET_REMOVED_UNKNOWN", lang)}</Box>}
    {/* Nicht lesbare Typen nennt removedInTargetPartial. */}
    {partial.length > 0 && <Box component="p" sx={{ m: 0, mt: 0.5 }}>{packageTransportText("PACKAGE_TRANSPORT_TARGET_REMOVED_PARTIAL", lang, { types: partial.join(", ") })}</Box>}
  </Alert>;
}
